import { memo, type ReactNode } from "react";
import type { Task } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import { TaskViewerUsers } from "@components/presence/TaskViewerUsers";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { hasFieldContent } from "@design-system/Board/BoardCardLayout";
import { SubScopeProvider } from "@state/subScopeContext";
import type { TaskBoardTask } from "../types";
import { TaskBoardCardBody } from "./TaskBoardCardBody";
import {
    TaskCardBlocked,
    TaskCardClient,
    TaskCardComments,
    TaskCardDueDate,
    TaskCardEntities,
    TaskCardOpenChat,
} from "./TaskBoardCardFields";
import { getOptionalCardField } from "./TaskBoardOptionalFields";
import {
    isTaskCardFieldId,
    type PrimaryCardFieldId,
    TASK_CARD_COMMENTS_FIELD,
    type TaskCardFieldId,
} from "./taskCardColumns";
import { getTaskCardContext, useTaskCardLabel } from "./taskCardContext";
import {
    getTaskColumnName,
    type TaskCardEdit,
    type TaskCardEditField,
} from "./taskCardEdits";

/** A field control a card shortcut opened on the card, until it closes; `title` is the title modal. */
export interface TaskCardShortcut {
    id: number;
    field: TaskCardEditField | "title";
}

export interface TaskBoardCardProps {
    task: TaskBoardTask;
    /**
     * The fields the card shows: List's visible columns as `TaskColumn` ids,
     * in List's order, then `comments` when the view shows them.
     */
    cardFields: readonly string[];
    /** The `TaskColumn` ids the columns and lanes already show, left off the card. */
    hiddenFields: ReadonlySet<string>;
    isCrossWorkspace: boolean;
    onOpen: (task: Task) => void;
    /** Single-card edits from the card's chips, shortcuts and title. */
    onEdit: (edit: TaskCardEdit) => void;
    /** Opens the task with its chat, from the Comments chip. */
    onOpenChat: (task: Task) => void;
    /** Opens the task with its Dependencies, from the blocked tag. */
    onOpenDependencies: (task: Task) => void;
    /** Opens the task with its Details section, from the menus' Edit details. */
    onEditDetails: (task: Task) => void;
    /** `inline` renames on the card (`taskTitleInlineEdit`), `modal` in the title modal. */
    renameMode: "inline" | "modal";
    shortcut: TaskCardShortcut | null;
    onShortcutEnd: (shortcutId: number) => void;
}

interface TaskFieldArgs {
    task: Task;
    clientWorkspaceId?: string;
}

/** The fields with their own place on the card; the rest are chips. */
const PRIMARY_CARD_FIELDS: Record<
    PrimaryCardFieldId,
    (args: TaskFieldArgs) => BoardCardField
> = {
    [TaskColumn.DUE_DATE]: ({ task }) => ({
        id: TaskColumn.DUE_DATE,
        placement: "inline",
        content: task.dueDate ? (
            <TaskCardDueDate
                task={{ dueDate: task.dueDate, status: task.status }}
            />
        ) : null,
    }),
    [TaskColumn.ENTITIES]: ({ task, clientWorkspaceId }) => ({
        id: TaskColumn.ENTITIES,
        placement: "stacked",
        content: task.entityIds?.length ? (
            <TaskCardEntities
                entityIds={task.entityIds}
                clientWorkspaceId={clientWorkspaceId}
            />
        ) : null,
    }),
};

const isPrimaryCardField = (
    field: TaskCardFieldId,
): field is PrimaryCardFieldId => Object.hasOwn(PRIMARY_CARD_FIELDS, field);

/** A card field's content; null for List columns the card has no field for. */
const getConfiguredField = ({
    field,
    ...args
}: TaskFieldArgs & { field: string }): BoardCardField | null => {
    if (!isTaskCardFieldId(field)) {
        return null;
    }
    return {
        ...(isPrimaryCardField(field)
            ? PRIMARY_CARD_FIELDS[field](args)
            : getOptionalCardField(args.task, field)),
        label: getTaskColumnName(field),
    };
};

type TaskCardFieldsArgs = Pick<
    TaskBoardCardProps,
    "task" | "cardFields" | "hiddenFields" | "isCrossWorkspace"
>;

/**
 * The card fields (List's visible columns), minus those the columns and lanes
 * already show, then the client on the organization page unless the columns or
 * lanes show it. The client joins the entities' context line when the card
 * shows them, else it stands alone.
 */
const getConfiguredCardFields = ({
    task,
    cardFields,
    hiddenFields,
    isCrossWorkspace,
}: TaskCardFieldsArgs): BoardCardField[] => {
    const shownFields = cardFields.filter((field) => !hiddenFields.has(field));
    const { clientWorkspaceId, entityIds } = getTaskCardContext({
        task,
        cardFields,
        hiddenFields,
        isCrossWorkspace,
    });
    return [
        {
            id: TaskColumn.CLIENT,
            placement: "stacked",
            content: clientWorkspaceId !== undefined &&
                entityIds.length === 0 && (
                    <TaskCardClient workspaceId={clientWorkspaceId} />
                ),
        },
        ...shownFields
            .map((field) =>
                getConfiguredField({ task, clientWorkspaceId, field }),
            )
            .filter((field) => field !== null),
    ];
};

type TaskCardDisplayFieldsArgs = TaskCardFieldsArgs &
    Pick<TaskBoardCardProps, "onOpenDependencies">;

/**
 * The card's fields as the full and compact cards show them: the blocked
 * tag that opens the task's Dependencies, the card fields in List's order
 * with the client's context line and, last, the unread comments (read only
 * here; none without unread messages). The status goes first through
 * `withStatusField`; each card adds its presence stack.
 */
export const getTaskCardDisplayFields = ({
    onOpenDependencies,
    ...args
}: TaskCardDisplayFieldsArgs): BoardCardField[] => {
    const blockedCount = args.task.numberOfActiveBlockedByTasks ?? 0;
    const unreadCount = args.task.unreadCount ?? 0;
    return [
        {
            id: "blocked",
            placement: "inline",
            label: "Blocked",
            content: blockedCount > 0 && (
                <TaskCardBlocked
                    task={args.task}
                    onClick={() => onOpenDependencies(args.task)}
                />
            ),
        },
        ...getConfiguredCardFields(args),
        {
            id: TASK_CARD_COMMENTS_FIELD,
            placement: "inline",
            label: "Comments",
            content: args.cardFields.includes(TASK_CARD_COMMENTS_FIELD) &&
                unreadCount > 0 && <TaskCardComments count={unreadCount} />,
        },
    ];
};

/** The card's fields, with the comments as the tag that opens the task's chat. */
const getCardFields = ({
    onOpenChat,
    ...args
}: TaskCardDisplayFieldsArgs &
    Pick<TaskBoardCardProps, "onOpenChat">): BoardCardField[] =>
    getTaskCardDisplayFields(args).map((field) =>
        field.id === TASK_CARD_COMMENTS_FIELD && hasFieldContent(field.content)
            ? {
                  ...field,
                  content: (
                      <TaskCardOpenChat
                          count={args.task.unreadCount ?? 0}
                          onClick={() => onOpenChat(args.task)}
                      />
                  ),
              }
            : field,
    );

/** On the organization page, reads data in the task's own workspace. */
export const ScopedToTaskWorkspace = ({
    task,
    isCrossWorkspace,
    children,
}: {
    task: Task;
    isCrossWorkspace: boolean;
    children: ReactNode;
}) =>
    isCrossWorkspace ? (
        <SubScopeProvider value={{ workspaceId: task.workspaceId }}>
            {children}
        </SubScopeProvider>
    ) : (
        children
    );

/**
 * The card body with its fields. Unlike List rows, cards keep showing their
 * viewers while a task is open, since the other cards stay in view beside it.
 */
const TaskBoardCardContent = (props: Readonly<TaskBoardCardProps>) => {
    const label = useTaskCardLabel(props);
    return (
        <TaskViewerUsers
            taskId={props.task.id}
            workspaceId={props.task.workspaceId}
        >
            {(viewers) => (
                <TaskBoardCardBody
                    {...props}
                    label={label}
                    fields={getCardFields(props)}
                    presence={{
                        viewers,
                        assignee: props.task.assignee ?? null,
                    }}
                />
            )}
        </TaskViewerUsers>
    );
};

/**
 * A task card. Clicking it opens the task like clicking its List row; its
 * chips, title and shortcuts edit it (see `TaskBoardCardBody`), the
 * Comments chip opens its chat and the blocked tag its Dependencies. On the organization page it reads data and
 * opens its controls in the task's workspace, as List rows do.
 */
export const TaskBoardCard = memo((props: Readonly<TaskBoardCardProps>) => (
    <ScopedToTaskWorkspace
        task={props.task}
        isCrossWorkspace={props.isCrossWorkspace}
    >
        <TaskBoardCardContent {...props} />
    </ScopedToTaskWorkspace>
));

TaskBoardCard.displayName = "TaskBoardCard";
